import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '../context/NotificationContext'
import type { Notification } from '../context/NotificationContext'

type Props = {
  open: boolean
  onClose: () => void
}

const getIcon = (type: Notification['type']) => {
  switch (type) {
    case 'bonus':
      return '🎁'
    case 'topup':
      return '💰'
    case 'promo':
      return '📢'
    default:
      return 'ℹ️'
  }
}

const timeAgo = (ts: number) => {
  const diff = Math.floor((Date.now() - ts) / 1000)
  if (diff < 60) return 'только что'
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч назад`
  return `${Math.floor(diff / 86400)} дн назад`
}

const NotificationPanel = ({ open, onClose }: Props) => {
  const { notifications, clearAll } = useNotifications()
  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  // Уведомление со ссылкой (10.09): клик ведёт к документу или работе, панель закрывается. Внешняя ссылка (рассылки
  // админки, 24.10) — в новой вкладке, как обещает подсказка «Куда ведёт»: человек не уходит с сайта.
  const openLink = (url: string) => {
    onClose()
    if (/^https?:\/\//.test(url)) window.open(url, '_blank', 'noopener,noreferrer')
    else navigate(url)
  }

  useEffect(() => {
    if (!open) return

    const handler = (e: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
      <div
          className="notification-panel"
          ref={panelRef}
          onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="notification-panel-header">
          <span className="notification-panel-title">Уведомления</span>
          {/* «Очистить» снимает ТОЛЬКО клиентские transient-уведомления.
              Серверные (персистентные — сгорание токенов, движение
              реф-баланса) остаются в разделе: это история про деньги, её
              нельзя терять по нажатию кнопки. См. clearAll в
              NotificationContext. */}
          {notifications.length > 0 && (
              <button className="notification-clear-btn" onClick={clearAll}>
                Очистить
              </button>
          )}
        </div>

        <div className="notification-panel-list">
          {notifications.length === 0 ? (
              <div className="notification-empty">Нет уведомлений</div>
          ) : (
              notifications.map(n => (
                  <div key={n.id} className={`notification-item ${n.read ? '' : 'notification-unread'}${n.url ? ' notification-item--link' : ''}`}
                       role={n.url ? 'link' : undefined} tabIndex={n.url ? 0 : undefined} title={n.url ? 'Открыть' : undefined}
                       onClick={n.url ? () => openLink(n.url as string) : undefined}
                       onKeyDown={n.url ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLink(n.url as string) } } : undefined}>
                    <span className="notification-icon">{getIcon(n.type)}</span>
                    <div className="notification-content">
                      <span className="notification-text">{n.text}</span>
                      <span className="notification-time">{timeAgo(n.createdAt)}{n.url ? ' · открыть →' : ''}</span>
                    </div>
                  </div>
              ))
          )}
        </div>
      </div>
  )
}

export default NotificationPanel